import * as React from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { Badge } from './badge';
import { Button } from './button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './command';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

export interface MultiSelectOption<T extends string = string> {
  value: T;
  label: string;
  disabled?: boolean;
}

interface MultiSelectProps<T extends string = string> {
  options: MultiSelectOption<T>[];
  value: T[];
  onChange: (next: T[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  className?: string;
  /** Cap the displayed selected chips; the rest collapse into "+N more". */
  maxChips?: number;
}

export function MultiSelect<T extends string = string>({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyMessage = 'No results',
  disabled,
  className,
  maxChips = 3,
}: MultiSelectProps<T>) {
  const [open, setOpen] = React.useState(false);

  const toggle = (v: T) => {
    if (value.includes(v)) onChange(value.filter((x) => x !== v));
    else onChange([...value, v]);
  };

  const remove = (v: T, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(value.filter((x) => x !== v));
  };

  const visibleChips = options.filter((o) => value.includes(o.value)).slice(0, maxChips);
  const overflow = value.length - visibleChips.length;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'flex h-auto min-h-9 w-full flex-wrap items-center justify-start gap-1 px-2 py-1.5 font-normal',
            value.length === 0 && 'text-muted-foreground',
            className,
          )}
        >
          {value.length === 0 ? (
            <span className="px-1">{placeholder}</span>
          ) : (
            <>
              {visibleChips.map((opt) => (
                <Badge key={opt.value} variant="secondary" className="gap-1 pr-0.5">
                  <span>{opt.label}</span>
                  <button
                    type="button"
                    onClick={(e) => remove(opt.value, e)}
                    className="rounded p-0.5 hover:bg-foreground/10"
                    aria-label={`Remove ${opt.label}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              {overflow > 0 && <Badge variant="outline">+{overflow}</Badge>}
            </>
          )}
          <ChevronsUpDown className="ml-auto h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => (
                <CommandItem
                  key={opt.value}
                  value={opt.label}
                  disabled={opt.disabled}
                  onSelect={() => toggle(opt.value)}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value.includes(opt.value) ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  {opt.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
